import React, { ReactNode, useMemo } from 'react';
import { DateTime } from 'luxon';
import { getTenantsWithFloor } from '../utils/TenantUtils';
import { useUnits } from './useUnits';
import { useFloors } from './useFloors';
import { useDevice } from './useDevice';
import { IconUserSquare, IconClipboard } from '../components/icons';
import { FormattedMessage } from 'react-intl';
import { Demographic, TenantWithFloor } from '../types/unit';

interface UseTenants {
  tenants: TenantWithFloor[];
  demographic: Demographic;
  tenantLinkProps: {
    icon: ReactNode;
    label: ReactNode;
  };
}

export const useTenants = (): UseTenants => {
  const { data: units } = useUnits();
  const { data: floors } = useFloors();
  const { data: device } = useDevice();

  const tenants = useMemo(() => {
    if (!units || !floors) return [];
    const now = DateTime.now();
    return getTenantsWithFloor(units, floors, device?.hardware_id).filter(
      (tenant) => {
        if (tenant.active_from && tenant.active_from > now) return false;
        if (tenant.active_to && tenant.active_to < now) return false;
        return true;
      },
    );
  }, [units, floors, device?.hardware_id]);

  const demographic = useMemo((): Demographic => {
    let companyCount = 0;
    let individualCount = 0;
    for (const tenant of tenants) {
      if (tenant.type === 'company') {
        companyCount += 1;
      } else {
        individualCount += 1;
      }
      if (companyCount > 0 && individualCount > 0) {
        return 'mixed';
      }
    }

    if (companyCount === 0) {
      return 'individuals';
    }
    return 'companies';
  }, [tenants]);

  const tenantLinkProps = useMemo(() => {
    switch (demographic) {
      case 'companies':
        return {
          icon: <IconClipboard />,
          label: <FormattedMessage defaultMessage="Företag" />,
        };
      case 'individuals':
        return {
          icon: <IconUserSquare />,
          label: <FormattedMessage defaultMessage="Boende" />,
        };

      default:
        return {
          icon: <IconClipboard />,
          label: <FormattedMessage defaultMessage="Hyresgäster" />,
        };
    }
  }, [demographic]);

  return { tenants, demographic, tenantLinkProps };
};
